import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Redux
import { useAppSelector } from 'hooks/useStore';
// Selectors
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Types
import QuizTypes from 'types/QuizTypes';
import MediaFormats from 'types/MediaFormats';
// MUI
import { ButtonGroup } from '@material-ui/core';
// Components
import { Input, Toggle } from 'components/Controls';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import QRCode from 'components/QRCode';
import ImageUpload from 'components/ImageUpload';
import StyledMuiButton from 'components/StyledMuiButton';
// i18next
import { useTranslation } from 'react-i18next';

const PublicLeaderboardQrCode:FC = () => {
  const { t } = useTranslation();

  const quiz = useAppSelector(selectQuiz);

  const { control, watch } = useFormContext();

  const isPollQuizzes = quiz && [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);

  const {
    enabled:publicLeaderboardEnabled,
    quizQRCodeEnabled:publicLeaderboardQuizQRCodeEnabled,
    quizQRCodeLink:publicLeaderboardQuizQRCodeLink,
    quizQRCodeLogo:publicLeaderboardQuizQRCodeLogo,
    quizQrCodeLogoFormat:publicLeaderboardQuizQrCodeLogoFormat
  } = watch('publicLeaderboard');

  if (!quiz || quiz.type === QuizTypes.Personality) return null;
  return (
    <div className="mt-8">
      {publicLeaderboardEnabled && (
        <Fragment>
          <div className="flex items-center mb-4">
            <span className="block text-white text-base font-semibold mr-3">{t('pages.quizForm.publicLeaderboard.qrCodeLabel')}</span>
            <Controller
              control={control} name="publicLeaderboard.quizQRCodeEnabled"
              render={({ field: { onChange, value } }) => (
                <Toggle
                  id="publicLeaderboardQuizQRCodeEnabled"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <span className="block text-gray-400 text-sm">
            {isPollQuizzes ? t('pages.quizForm.publicLeaderboard.qrCodePollHint') : t('pages.quizForm.publicLeaderboard.qrCodeHint')}
          </span>
          {publicLeaderboardQuizQRCodeEnabled && (
            <Fragment>
              <QRCode
                link={publicLeaderboardQuizQRCodeLink}
                quizName={quiz.quizName}
                quizNameSlug={quiz.quizNameSlug}
                qrCodeLogo={publicLeaderboardQuizQRCodeLogo || undefined}
                showDownloadButton
              />
              <div className="pt-4">
                <MultiLanguageTextField
                  name="publicLeaderboard.quizQRCodeTitle"
                  label={t('pages.quizForm.publicLeaderboard.qrCodeTitleLabel')}
                  helperText={t('pages.quizForm.publicLeaderboard.qrCodeTitleHint')}
                  className="mb-4"
                />
                <Controller
                  control={control} name="publicLeaderboard.quizQRCodeLink"
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('pages.quizForm.publicLeaderboard.qrCodeLinkLabel')} id="publicLeaderboard.quizQRCodeLink"
                      helperText={t('pages.quizForm.publicLeaderboard.qrCodeLinkHint')}
                    />
                  )}
                />
                <Controller
                  control={control} name="publicLeaderboard.quizQrCodeLogoFormat"
                  render={({ field: { onChange, value } }) => (
                    <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                      <StyledMuiButton
                        variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                        onClick={() => onChange(MediaFormats.Logo)}
                      >{t('labels.squareLogo')}</StyledMuiButton>
                      <StyledMuiButton
                        variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                        onClick={() => onChange(MediaFormats.RectLogo)}
                      >{t('labels.rectangularLogo')}</StyledMuiButton>
                    </ButtonGroup>
                  )}
                />
                <Controller
                  control={control} name="publicLeaderboard.quizQRCodeLogo"
                  render={({ field }) => (
                    <ImageUpload
                      {...field}
                      key={publicLeaderboardQuizQrCodeLogoFormat}
                      format={publicLeaderboardQuizQrCodeLogoFormat}
                      label="pages.quizForm.publicLeaderboard.qrCodeLogoLabel"
                      helperText={t('pages.quizForm.publicLeaderboard.qrCodeLogoHint')}
                      className="sm:w-1/4"
                    />
                  )}
                />
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
    </div>
  )
}

export default PublicLeaderboardQrCode;
